import React from 'react';
import { IQuestionConfigurator } from '../../survey.types';
import { Validator } from '../validator';

export class QuestionConfigurator extends IQuestionConfigurator {
  constructor({
    serverContext, configuration, i18n, surveyConfigurator,
  }) {
    super({});
    this.serverContext = serverContext;
    const {
      data,
      key,
      position,
    } = configuration;
    this.key = key;
    this.position = position;
    this.data = data;
    this.configuration = configuration;
    this.i18n = i18n;
    this.surveyConfigurator = surveyConfigurator;
    this.initValidator();
  }

  // polymorphism of the view
  get view(): (props: any) => JSX.Element {
    throw new Error('Not implemented');
  }

  // polymorphism of the validator
  initValidator() {
    this.validator = new Validator();
  }

  // determine if question is valid, delegating to validator
  isValid(answers: Object) {
    const isVisible = this.isVisible(answers);
    if (isVisible) {
      const data = answers[this.key];
      return this.validator.validate({ data, configuration: this.configuration });
    }
    return true;
  }

  // determine if question is valid, delegating to validator
  getError(data: Object) {
    return this.validator.getError({
      data,
      configuration: this.configuration,
      i18n: this.i18n,
    });
  }

  // determine if question is answered, delegating to validator
  isAnswered(data: Object) {
    return this.validator.isAnswered({
      data,
      configuration: this.configuration,
      i18n: this.i18n,
    });
  }

  // determine if question is valid, delegating to configuration
  isVisible(answers: Object) {
    if (typeof this.configuration.isVisible === 'function') {
      return this.configuration.isVisible(answers, this.surveyConfigurator);
    }
    return true;
  }

  // get the title of the question, delegating to configuration
  getTitle(answers: Object) {
    return this.configuration.getTitle(answers, this.surveyConfigurator);
  }

  // get the description of the question, delegating to configuration
  getDescription(answers: Object) {
    if (this.configuration.getDescription === undefined) {
      return null;
    }
    return this.configuration.getDescription(answers, this.surveyConfigurator);
  }

  // get the original configuration for customized purposes
  getConfiguration() {
    return this.configuration;
  }

  // get the default data for the question
  getDefaultData(): Object {
    return {};
  }

  getDefaultDirtyState(bool: boolean): Object {
    return bool;
  }

  // get the functional polymorphism of the view
  getView(): (props: any) => JSX.Element {
    const View = this.view;
    return props =>
      (<View
        {...props}
        configurator={this}
      />);
  }

  // email is a special case of question and has a specific entry for OCV service
  isEmail() {
    return this.key === 'email';
  }

  // rating is a special case of question and has a specific entry for OCV service
  isRating() {
    return this.key === 'rating';
  }

  isComment() {
    return false;
  }

  // get the feedback data for sending to the OCV service
  getFeedbackData(answers: any): any {
    const data = answers[this.key];
    return data;
  }
}
